// @flow

import * as React from 'react';
import {defineMessages, useIntl, FormattedMessage} from 'react-intl';
import {useAccount, useApi} from 'docs/data';
import {
  CHECKOUT_CURRENCIES,
  SUBUNIT_TO_UNITS,
  useMerchantCurrency,
  useFormatPriceAmount,
} from 'docs/hooks';
import {isBrowser} from 'docs/util';
import Box from 'sail/Box';
import {AlignBox} from 'sail/Align';
import Group from 'sail/Group';
import {BodyAlt, HeadingTextProps} from 'sail/Text';
import TruncatedText from 'sail/TruncatedText';
import Icon from 'sail/Icon';
import {Button, SubmitButton, ButtonLink} from 'sail/Button';
import ContentTabs, {ContentTab} from 'sail/ContentTabs';
import FormLayout, {FormRow, FormField} from 'sail/FormLayout';
import {TextInput, InputGroup} from 'sail/Input';
import Select, {SelectOption} from 'sail/Select';
import {SearchableSelect} from 'sail/Search';
import {InfoTooltip} from 'sail/Tooltip';
import type {SearchableSelectResultProps} from 'sail/Search';

import {useData} from '../../data/DataProvider';
import type {DataContextProps} from '../../data/GuideTypes';

import '../../css/CreatePriceForm.scss';

type Props = {|
  type: 'default' | 'billing',
  prices: $PropertyType<DataContextProps, 'prices'>,
|};

const messages = defineMessages({
  oneTime: {
    id: 'docsShell.ib.checkout.html.line-items.option.oneTime',
    defaultMessage: 'One-time',
  },
  recurring: {
    id: 'docsShell.ib.checkout.html.line-items.option.recurring',
    defaultMessage: 'Recurring',
  },
  error: {
    id: 'docsShell.ib.checkout.html.line-items.option.error',
    defaultMessage: 'An unexpected error occurred. Please try again.',
  },
  chooseLabel: {
    id: 'docsShell.ib.checkout.html.line-items.option.chooseLabel',
    defaultMessage: 'Select a test product or create a new one',
  },
  addProduct: {
    id: 'docsShell.ib.checkout.html.line-items.option.addProduct',
    defaultMessage: 'Add new test product',
  },
  createProduct: {
    id: 'docsShell.ib.checkout.html.line-items.option.createProduct',
    defaultMessage: 'Create test product',
  },
  createProductTooltip: {
    id: 'docsShell.ib.checkout.html.line-items.option.createProductTooltip',
    defaultMessage:
      'When you’re ready to go live, remember to update this with a live mode product.',
  },
  learnMore: {
    id: 'docsShell.ib.checkout.html.line-items.option.learnMore',
    defaultMessage: 'Learn more',
  },
  cancel: {
    id: 'docsShell.ib.checkout.html.line-items.option.cancel',
    defaultMessage: 'Cancel',
  },
  moreProductOptions: {
    id: 'docsShell.ib.checkout.html.line-items.option.moreProductOptions',
    defaultMessage: 'More product options',
  },
  name: {
    id: 'docsShell.ib.checkout.html.line-items.option.name',
    defaultMessage: 'Name',
  },
  namePlaceholder: {
    id: 'docsShell.ib.checkout.html.line-items.option.namePlaceholder',
    defaultMessage: 'Sunglasses, premium plan, etc.',
  },
  price: {
    id: 'docsShell.ib.checkout.html.line-items.option.price',
    defaultMessage: 'Price',
  },
  billingPeriod: {
    id: 'docsShell.ib.checkout.html.line-items.option.billingPeriod',
    defaultMessage: 'Billing period',
  },
  lookupKey: {
    id: 'docsShell.ib.checkout.html.line-items.option.lookupKey',
    defaultMessage: 'Lookup key',
  },
  lookupKeyTooltip: {
    id: 'docsShell.ib.checkout.html.line-items.option.lookupKeyTooltip',
    defaultMessage:
      'A lookup key used to retrieve prices dynamically from a static string.',
  },
  day: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.day',
    defaultMessage: 'Daily',
  },
  month: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.month',
    defaultMessage: 'Monthly',
  },
  year: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.year',
    defaultMessage: 'Yearly',
  },
  week: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.week',
    defaultMessage: 'Weekly',
  },
  custom: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.custom',
    defaultMessage: 'Custom',
  },
  quarter: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.quarter',
    defaultMessage: 'Every 3 months',
  },
  semiannual: {
    id: 'docsShell.ib.checkout.html.line-items.billing-period.semiannual',
    defaultMessage: 'Every 6 months',
  },
});

const INPUT_WIDTH = 236;
const TABS = {
  oneTime: {
    label: messages.oneTime,
  },
  recurring: {
    label: messages.recurring,
  },
};

const billingPeriodOptions = [
  'day',
  'week',
  'month',
  'quarter',
  'semiannual',
  'year',
];

const searchProps = {
  onChangeQuery: () => {},
  queryString: '',
  status: 'completed',
};

const ADD = '__ADD__';

function Item({highlighted, label, icon = undefined}) {
  return (
    <Box
      style={{
        overflow: 'hidden',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Box
        background={highlighted ? 'blue' : 'white'}
        style={{
          borderRadius: 3,
          width: '100%',
        }}
        padding={{horizontal: 8, vertical: 2}}
      >
        <AlignBox reference={HeadingTextProps} style={{alignItems: 'center'}}>
          <Group direction="horizontal" spacing={8}>
            {icon ? (
              <Icon
                icon={icon}
                color={highlighted ? 'white' : undefined}
                size={12}
              />
            ) : null}
            <BodyAlt color={highlighted ? 'white' : undefined}>
              <TruncatedText>{label}</TruncatedText>
            </BodyAlt>
          </Group>
        </AlignBox>
      </Box>
    </Box>
  );
}

function PricePicker({type, prices}: Props) {
  const intl = useIntl();
  const {account} = useAccount();
  const {api} = useApi();
  const defaultCurrency = useMerchantCurrency();
  const {
    activePrice: selectedPrice,
    setActivePrice: setSelectedPrice,
    setPrices,
  } = useData();
  const formatPriceAmount = useFormatPriceAmount();

  const [showPicker, setShowPicker] = React.useState(false);
  const [tab, setTab] = React.useState(
    type === 'billing' ? 'recurring' : 'oneTime',
  );

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [name, setName] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [currency, setCurrency] = React.useState(
    defaultCurrency ? defaultCurrency.toUpperCase() : 'USD',
  );
  const [billingPeriod, setBillingPeriod] = React.useState('month');
  const [lookupKey, setLookupKey] = React.useState('');

  const getButtonLabel = (entry) => {
    if (entry.id === ADD) {
      return intl.formatMessage(messages.addProduct);
    }
    return `${entry.product.name} — ${formatPriceAmount(entry)}`;
  };

  React.useEffect(() => {
    /**
     * Shift to show the product picker if prices are loaded.
     * This will also fire after a product has been created,
     * shifting back to the product picker.
     */
    if (prices.length) {
      setShowPicker(true);
    }
  }, [prices]);

  if (!account.merchant?.isLoggedIn) {
    return null;
  }

  if (showPicker) {
    return (
      <Group
        margin={{top: 8, bottom: 16}}
        direction="horizontal"
        alignItems="center"
        spacing={8}
      >
        <SearchableSelect
          {...searchProps}
          className="PricePicker"
          results={[{id: ADD}, ...prices]}
          autoflip
          chooseLabel={intl.formatMessage(messages.chooseLabel)}
          onChange={(price) => {
            if (price?.id === ADD) {
              return setShowPicker(false);
            }
            setSelectedPrice(price);
          }}
          value={selectedPrice}
          width="long"
          renderInput={() => null}
          renderItem={(props: SearchableSelectResultProps<Object>) => (
            <Item
              highlighted={props.highlighted}
              label={getButtonLabel(props.entry)}
              icon={props.entry.id === ADD ? 'add' : undefined}
            />
          )}
          getButtonLabel={getButtonLabel}
          getFormValue={(entry) => entry.id}
        />
        <InfoTooltip
          allowMouseOverContents
          content={
            <>
              <FormattedMessage {...messages.createProductTooltip} />{' '}
              <ButtonLink
                display="inline-flex"
                label={<FormattedMessage {...messages.learnMore} />}
                icon="external"
                iconPosition="right"
                target="_blank"
                href="/docs/billing/prices-guide#create-product"
              />
            </>
          }
        />
      </Group>
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let interval = billingPeriod;
      let interval_count = 1;
      if (interval === 'quarter') {
        interval = 'month';
        interval_count = 3;
      } else if (interval === 'semiannual') {
        interval = 'month';
        interval_count = 6;
      }

      const subUnitToUnit = SUBUNIT_TO_UNITS[currency.toUpperCase()] || 100;
      const response = await api.createProductAndPrice({
        name,
        unit_amount: Math.floor(Number(amount) * subUnitToUnit),
        currency,
        recurring: tab === 'recurring' ? {interval, interval_count} : undefined,
        lookup_key: lookupKey,
      });
      if (response) {
        if (response.error) {
          setError(response.error);
        } else if (response.id) {
          setSelectedPrice(response);
          setPrices((current) => [response, ...current]);

          setName('');
          setAmount('');
        }
      }
    } catch {
      setError(intl.formatMessage(messages.error));
    } finally {
      setLoading(false);
    }
  }

  const {maximumFractionDigits, minimumFractionDigits} = typeof isBrowser
    ? new Intl.NumberFormat(account.locale?.code, {
        style: 'currency',
        currency,
      }).resolvedOptions()
    : {maximumFractionDigits: 2, minimumFractionDigits: 2};

  return (
    <Box
      flex={{direction: 'column'}}
      margin={{bottom: 16}}
      className="ElementsEditor"
    >
      {type === 'default' ? (
        <ContentTabs size="medium">
          {Object.keys(TABS).map((key) => (
            <ContentTab
              key={key}
              onClick={(evt) => setTab(key)}
              active={tab === key}
            >
              <FormattedMessage {...TABS[key].label} />
            </ContentTab>
          ))}
        </ContentTabs>
      ) : null}
      <form onSubmit={onSubmit}>
        <FormLayout
          layout="stacked"
          divider={false}
          size="small"
          style={{background: 'transparent'}}
        >
          <FormRow
            label={intl.formatMessage(messages.name)}
            style={{padding: '20px 0 0'}}
          >
            <FormField>
              <TextInput
                required
                aria-label={intl.formatMessage(messages.name)}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                value={name}
                onChange={setName}
                width={INPUT_WIDTH}
                maxLength={250}
              />
            </FormField>
          </FormRow>
          <FormRow
            label={intl.formatMessage(messages.price)}
            style={{padding: '20px 0 0'}}
          >
            <FormField>
              <InputGroup direction="horizontal" invalid={false}>
                <TextInput
                  required
                  start={{
                    'aria-label': currency,
                    content: intl
                      .formatNumber(0, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0,
                      })
                      .replace('0', ''),
                  }}
                  placeholder={intl.formatNumber(0.0, {
                    style: 'decimal',
                    minimumFractionDigits,
                    maximumFractionDigits,
                  })}
                  type="number"
                  value={amount}
                  min={0}
                  max={999999999999}
                  step=".01"
                  onChange={(newAmount) => {
                    const match = newAmount.match(/^-?\d+(?:[.,]\d{0,2})?/);

                    setAmount(match ? match[0] : newAmount);
                  }}
                />
                <Select
                  required
                  defaultValue="usd"
                  value={currency}
                  onChange={setCurrency}
                >
                  {CHECKOUT_CURRENCIES.map((c) => (
                    <SelectOption key={c} value={c} label={c} />
                  ))}
                </Select>
              </InputGroup>
            </FormField>
          </FormRow>
          {tab === 'recurring' && (
            <FormRow
              label={intl.formatMessage(messages.billingPeriod)}
              style={{padding: '20px 0 0'}}
            >
              <FormField>
                <Select
                  required
                  defaultValue="usd"
                  value={billingPeriod}
                  onChange={setBillingPeriod}
                  width={INPUT_WIDTH}
                >
                  {billingPeriodOptions.map((key) => (
                    <SelectOption
                      key={key}
                      value={key}
                      label={intl.formatMessage(messages[key])}
                    />
                  ))}
                </Select>
              </FormField>
            </FormRow>
          )}
          {type === 'billing' && (
            <FormRow
              label={
                <Group alignItems="center" direction="horizontal" spacing={8}>
                  {intl.formatMessage(messages.lookupKey)}
                  <InfoTooltip
                    content={intl.formatMessage(messages.lookupKeyTooltip)}
                  />
                </Group>
              }
              style={{padding: '20px 0 0'}}
            >
              <FormField>
                <TextInput
                  required
                  value={lookupKey}
                  onChange={setLookupKey}
                  aria-label={intl.formatMessage(messages.lookupKey)}
                />
              </FormField>
            </FormRow>
          )}
          <FormRow style={{padding: '28px 0 0'}}>
            <FormField error={error}>
              <Group
                spacing={8}
                direction="horizontal"
                justifyContent="space-between"
                alignItems="center"
              >
                <Group spacing={12} direction="horizontal" alignItems="center">
                  <SubmitButton
                    label={<FormattedMessage {...messages.createProduct} />}
                    icon="add"
                    pending={loading}
                  />
                  {prices.length ? (
                    <Button
                      label={<FormattedMessage {...messages.cancel} />}
                      disabled={loading}
                      onClick={() => setShowPicker(true)}
                    />
                  ) : null}
                </Group>
                <ButtonLink
                  label={<FormattedMessage {...messages.moreProductOptions} />}
                  icon="external"
                  iconPosition="right"
                  target="_blank"
                  href="https://dashboard.stripe.com/test/products/create"
                />
              </Group>
            </FormField>
          </FormRow>
        </FormLayout>
      </form>
    </Box>
  );
}

export function DefaultPricePicker() {
  const {prices} = useData();
  return <PricePicker type="default" prices={prices} />;
}

export function BillingPricePicker() {
  const {prices} = useData();
  return (
    <PricePicker
      type="billing"
      prices={prices.filter((price) => price.lookup_key != null)}
    />
  );
}
